// Rspack allows us to use process.env. You can also define this in a .env file
// as VITE_API_BASE_URL or similar, but for standard Rspack setups, this works.
export const API_BASE_URL = process.env.API_BASE_URL || '/api/v1'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export type ApiParams = Record<string, string | number | boolean | undefined>

interface ApiRequestOptions {
  method?: HttpMethod
  body?: unknown
  params?: ApiParams
  headers?: Record<string, string>
}

export class ApiError extends Error {
  status: number
  details?: unknown
  constructor(message: string, status: number, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
  }
}

// Build the absolute URL for an API endpoint with optional query params.
export function apiUrl(endpoint: string, params?: ApiParams): string {
  const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin)
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value))
      }
    })
  }
  return url.toString()
}

// While the offline outbox drains in the background (lib/offline.ts), a 401
// means the session expired while the user was offline. Hard-navigating to
// /login mid-replay would lose the queue UI, so the drain suppresses the
// redirect and surfaces the failure through the mutation instead.
let suppress401Redirect = false

export function setSuppress401Redirect(value: boolean): void {
  suppress401Redirect = value
}

// Translate a non-2xx response into an ApiError, unwrapping the backend's
// {"error"|"message"} JSON when present. An expired/invalid session means the
// user must log in again — redirect, except for the login endpoints themselves
// (bad credentials there are an expected 401, not a session expiry).
export async function raiseApiError(response: Response, endpoint: string): Promise<never> {
  let errorDetails
  let errorMessage = `Request failed with status ${response.status}`
  try {
    errorDetails = await response.json()
    // Attempt to extract a specific message from the backend (e.g. Go JSON response)
    if (errorDetails?.message || errorDetails?.error) {
      errorMessage = errorDetails.message || errorDetails.error
    }
  } catch {
    // Response wasn't JSON, ignore parsing error
  }
  if (
    !suppress401Redirect &&
    response.status === 401 &&
    !window.location.pathname.startsWith('/login') &&
    !window.location.pathname.startsWith('/reset-password') &&
    !endpoint.startsWith('/auth/login') &&
    !endpoint.startsWith('/auth/passkey/login') &&
    !endpoint.startsWith('/auth/password-reset')
  ) {
    // Tell the login page why the user landed here and where to send them
    // back after signing in.
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.assign(`/login?expired=1&returnTo=${returnTo}`)
  }
  throw new ApiError(errorMessage, response.status, errorDetails)
}

export async function apiClient<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, headers = {} } = options

  const url = apiUrl(endpoint, params)

  // FormData bodies are passed through untouched so the browser sets the
  // multipart boundary Content-Type; everything else is JSON.
  const isFormData = body instanceof FormData
  const finalHeaders: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...headers,
  }

  // Authentication rides the fp_session HttpOnly cookie (same-origin fetch
  // sends cookies by default) — no Authorization header to attach.

  const response = await fetch(url, {
    method,
    headers: finalHeaders,
    body: body ? (isFormData ? (body as FormData) : JSON.stringify(body)) : undefined,
  })

  if (!response.ok) {
    await raiseApiError(response, endpoint)
  }

  // Handle 204 No Content (common for DELETE requests)
  if (response.status === 204) {
    return undefined as T
  }

  // Parse and return JSON response
  return response.json() as Promise<T>
}

// Origin serving API traffic, without the /api/v1 prefix. Used to build URLs
// for non-API routes (e.g. /media/... image bytes) that <img> tags load.
const API_ORIGIN = API_BASE_URL.replace(/\/api\/v1$/, '')

export const assetUrl = (path: string): string => `${API_ORIGIN}${path}`
